// use-hl-leaderboard — Snapshot des Hyperliquid-Wallet-Leaderboards.
// Endpoint: stats-data.hyperliquid.xyz (eine große JSON-Datei ~10k Wallets).
// Refresh-Intervall: 5min (Leaderboard ändert sich nicht sekündlich + großer
// Payload). Caller kann via refetch() manuell forcen.
//
// Filter + Sort passieren im Caller — der Hook liefert nur die rohe Liste.
// EVE-Score via `eveScore()`-Helper aus lib/hl-info-api.

import { useCallback, useEffect, useState } from 'react';
import { fetchLeaderboard, type LeaderboardRow } from '@/lib/hl-info-api';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export interface UseHlLeaderboardOptions {
  enabled?: boolean;
  pollIntervalMs?: number;
}

export interface UseHlLeaderboardReturn {
  rows: LeaderboardRow[];
  loading: boolean;
  refetch: () => Promise<void>;
  /** Unix-ms letzter erfolgreicher Fetch. null bis erstes Load. */
  lastFetchedAt: number | null;
}

export function useHlLeaderboard(
  options: UseHlLeaderboardOptions = {},
): UseHlLeaderboardReturn {
  const { enabled = true, pollIntervalMs = POLL_INTERVAL_MS } = options;
  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState<number | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const next = await fetchLeaderboard();
      if (next.length > 0) {
        setRows(next);
        setLastFetchedAt(Date.now());
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const poll = async (): Promise<void> => {
      if (cancelled) return;
      setLoading(true);
      try {
        const next = await fetchLeaderboard();
        if (!cancelled && next.length > 0) {
          setRows(next);
          setLastFetchedAt(Date.now());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void poll();
    const id = setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, pollIntervalMs]);

  return { rows, loading, refetch, lastFetchedAt };
}
